import { Controller } from "@hotwired/stimulus"

const TEXT_INPUTS = ["text", "email", "tel", "url", "number", "search"]

// The four-step registration wizard (docs/07 "Four steps inside one form").
//
// It is ONE form and ONE POST. The steps are panels shown and hidden here, so
// nothing is half-saved and Back never loses input — there is nothing to lose,
// because every field stays in the document the whole time.
//
// Three things it has to get right, and all three are invisible when wrong:
//
//   **Focus follows the step.** A keyboard user who presses Continue and has
//   focus left on a button that is now hidden has been dropped at the top of
//   the document. Focus moves to the new step's heading and the live region
//   says which step it is (T17c).
//
//   **The summary is never stale.** It is the only place the fee and the
//   chosen booths are visible from step 3, and a booth that went while
//   somebody was typing has to show there.
//
//   **The server decides which step reopens after an error.** `currentValue`
//   arrives from the server, which knows where the first error is; guessing
//   here would land people on a page that looks fine.
export default class extends Controller {
  static targets = [
    "panel", "stepperItem", "back", "continue", "heading", "live",
    "summaryZone", "summaryBooths", "summaryFee", "summaryBar", "progress", "progressLabel",
    "lost", "submit", "uploadNotice", "opportunitySet", "opportunityTotal", "feeNote"
  ]

  static values = {
    current: { type: Number, default: 1 },
    zoneChosen: { type: Boolean, default: false },
    boothChosen: { type: Boolean, default: false }
  }

  connect() {
    this.show(this.currentValue, { announce: false, focus: false })

    // Enter in a text field before the last step continues, as the button
    // does: the form is novalidate, so an implicit submit would post it
    // half-checked. A file field keeps Enter for its chooser.
    this.onEnter = (event) => {
      if (event.key !== "Enter" || event.isComposing || this.currentValue >= this.panelTargets.length) return
      if (event.target.tagName !== "INPUT" || !TEXT_INPUTS.includes(event.target.type)) return

      event.preventDefault()
      this.next()
    }
    this.element.addEventListener("keydown", this.onEnter)

    // A zone already chosen (a re-render after an error, or a restored
    // draft) has to bring its own opportunity list with it.
    const chosen = this.element.querySelector('input[name="exhibitor_registration[zone_id]"]:checked')
    if (chosen) this.showOpportunities(chosen.dataset.opportunitySet)

    // `prepared-details` connects after this controller and restores a saved
    // draft into the inputs; recount once it has, or the total says 0 beside
    // numbers the person can see.
    requestAnimationFrame(() => this.countOpportunities())
  }

  disconnect() {
    this.element.removeEventListener("keydown", this.onEnter)
  }

  // A step's fields are checked on Continue, while they can be seen; the form
  // is novalidate, so nothing checks them at Submit (a hidden panel).
  next() {
    const panel = this.panelTargets.find((target) => Number(target.dataset.step) === this.currentValue)
    const invalid = panel && Array.from(panel.querySelectorAll("input, textarea, select"))
      .find((field) => !field.disabled && !field.checkValidity())
    if (invalid) {
      invalid.reportValidity()
      return
    }

    this.show(this.currentValue + 1)
  }
  back() { this.show(this.currentValue - 1) }

  // The "Edit" links on the review step.
  goTo(event) {
    event.preventDefault()
    this.show(Number(event.currentTarget.dataset.step))
  }

  show(step, { announce = true, focus = true } = {}) {
    const total = this.panelTargets.length
    if (step < 1 || step > total) return

    this.currentValue = step

    this.panelTargets.forEach((panel) => {
      const mine = Number(panel.dataset.step) === step
      panel.hidden = !mine
    })

    this.stepperItemTargets.forEach((item) => {
      const number = Number(item.dataset.step)
      item.dataset.state = number < step ? "done" : number === step ? "current" : "todo"
      if (number === step) item.setAttribute("aria-current", "step")
      else item.removeAttribute("aria-current")
    })

    this.backTarget.hidden = step === 1
    this.continueTarget.hidden = step === total
    this.updateProgress(step, total)
    this.updateContinue()

    if (focus) this.focusHeading(step)
    if (announce) this.say(`Step ${step} of ${total}, ${this.headingText(step)}`)
  }

  focusHeading(step) {
    const heading = this.headingTargets.find((each) => Number(each.dataset.step) === step)
    heading?.focus({ preventScroll: false })
  }

  headingText(step) {
    const heading = this.headingTargets.find((each) => Number(each.dataset.step) === step)
    return heading ? heading.textContent.trim() : ""
  }

  updateProgress(step, total) {
    const percent = Math.round((step / total) * 100)
    if (this.hasProgressTarget) {
      this.progressTarget.style.width = `${percent}%`
      this.progressTarget.parentElement?.setAttribute("aria-label", `Step ${step} of ${total}`)
    }
    if (this.hasProgressLabelTarget) {
      this.progressLabelTarget.textContent = `STEP ${step} / ${total} · ${percent}% COMPLETE`
    }
  }

  // Continue is a promise that the next step will work. Step 1 without a zone
  // and step 2 without a booth cannot keep it, so the button says so rather
  // than letting somebody walk into an empty picker.
  updateContinue() {
    const blocked =
      (this.currentValue === 1 && !this.zoneChosenValue) ||
      (this.currentValue === 2 && !this.boothChosenValue)

    this.continueTarget.disabled = blocked
  }

  // --- What the steps report back -------------------------------------------

  chooseZone(event) {
    const radio = event.currentTarget
    this.zoneChosenValue = true
    this.updateContinue()

    // EVERY summary target, not the first: the rail and the review step both
    // show the zone, and `summaryZoneTarget` returns whichever is earlier in
    // the document — which is the review one, inside the form. Updating only
    // that left the rail saying "Not chosen yet" for the whole wizard.
    this.summaryZoneTargets.forEach((target) => {
      target.textContent = radio.dataset.zoneName
      target.closest("div")?.classList.remove("pending")
    })

    this.showOpportunities(radio.dataset.opportunitySet)

    // On `document`, not on this element: the picker is a DESCENDANT, and a
    // bubbling event from an ancestor never reaches it.
    this.dispatch("zone", {
      detail: { slug: radio.dataset.zoneSlug }, prefix: "registration", target: document
    })
  }

  // "Opportunities Provided" asks the chosen zone's list (D38). The other
  // lists are DISABLED as well as hidden, so their numbers are not posted.
  showOpportunities(set) {
    this.opportunitySetTargets.forEach((fieldset) => {
      const mine = fieldset.dataset.opportunitySet === set
      fieldset.hidden = !mine
      fieldset.disabled = !mine
    })
    this.countOpportunities()
  }

  countOpportunities() {
    // Each list's own total, found inside it: the Market list has none, so
    // pairing lists and totals by position put numbers under the wrong list.
    this.opportunitySetTargets.forEach((fieldset) => {
      const label = fieldset.querySelector('[data-registration-steps-target="opportunityTotal"]')
      if (!label) return

      const total = Array.from(fieldset.querySelectorAll('input[name$="[count]"]'))
        // Whole numbers only, as the server reads them: "1e5" is not 1.
        .reduce((sum, input) => sum + (/^\d+$/.test(input.value.trim()) ? Number(input.value) : 0), 0)
      label.textContent = `${total} ${total === 1 ? "opportunity" : "opportunities"} in all`
    })
  }

  // Fired by the booth picker (`booth-picker:change`). The picker does not
  // know what a wizard is; this is the wizard listening.
  boothsChanged(event) {
    const { booths, feeMinor } = event.detail

    this.boothChosenValue = booths.length > 0
    this.updateContinue()
    this.renderSummaryBooths(booths, feeMinor)
  }

  // A booth went while somebody was on a later step. The picker has already
  // let go of it; the summary is where they find out, and it offers the way
  // back rather than leaving them to work out which step the picker was on
  // (docs/07 "Persistent summary").
  boothLost(event) {
    this.lostCodes = event.detail.codes

    this.summaryBoothsTargets.forEach((target) => {
      target.textContent = this.lostCodes.map((code) => `${code} — no longer available`).join(", ")
      target.closest("div")?.classList.remove("pending")
    })

    this.lostTargets.forEach((target) => { target.hidden = false })
  }

  // The logo is optional, so an upload in progress never blocks Continue —
  // only the submit waits, and it says why rather than looking broken
  // (docs/07 "Logo upload states").
  uploadBusy() {
    this.submitTargets.forEach((target) => { target.disabled = true })
    this.uploadNoticeTargets.forEach((target) => { target.hidden = false })
  }

  uploadIdle() {
    this.submitTargets.forEach((target) => { target.disabled = false })
    this.uploadNoticeTargets.forEach((target) => { target.hidden = true })
  }

  chooseAnother() {
    this.lostCodes = []
    this.lostTargets.forEach((target) => { target.hidden = true })
    this.show(2)
  }

  renderSummaryBooths(booths, feeMinor) {
    const empty = booths.length === 0

    // A booth the picker just let go of has a message of its own, and the
    // change event that follows it says "nothing chosen" — which would wipe
    // the only explanation the person has.
    if (empty && this.lostCodes?.length > 0) return

    this.summaryBoothsTargets.forEach((target) => {
      target.closest("div")?.classList.toggle("pending", empty)
      // The LIVE status, not the one it had when it was picked: a booth taken
      // while somebody was typing in step 3 has to show here, because the
      // summary is the only place they can see it from (docs/07).
      target.textContent = empty
        ? "Not chosen yet"
        : booths.map((booth) => (booth.status === "available" ? booth.code
                                                              : `${booth.code} — no longer available`)).join(", ")
    })

    const fee = this.feeText(booths.length, feeMinor)
    this.summaryFeeTargets.forEach((target) => { target.textContent = empty ? "—" : fee })
    // The note under the fee follows the zone, not the page's first render.
    this.feeNoteTargets.forEach((note) => { note.hidden = (note.dataset.fee === "free") !== (feeMinor === 0) })

    if (this.hasSummaryBarTarget) {
      const noun = booths.length === 1 ? "booth" : "booths"
      this.summaryBarTarget.textContent = empty ? "No booths chosen" : `${booths.length} ${noun} · ${fee}`
    }
  }

  feeText(count, feeMinor) {
    if (feeMinor === 0) return "Free"

    const each = (feeMinor / 100).toLocaleString("en-US")
    const total = ((feeMinor * count) / 100).toLocaleString("en-US")

    return `${count} × MVR ${each} = MVR ${total}`
  }

  say(message) {
    if (this.hasLiveTarget) this.liveTarget.textContent = message
  }
}
